import {createSelector} from 'reselect';
import find from 'lodash/find';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';
import {getPublicationGroupParentId} from 'utilities/URL';
import {ROOT_PAGE_IDS} from 'constants/ObjectsConstants';

export const selectPages = state => state.navigation.pages;

const flatten = (pages = []) => {
  const iterator = (currentPages, agg) => {
    if (!currentPages) return agg;

    currentPages.forEach(page => {
      agg.push(page);
      iterator(page.children, agg);
    });

    return agg;
  };

  return iterator(pages, []);
};

export const selectFlattenedMergedPages = createSelector(selectPages, flatten);

const reselectPageById = createSelector(selectFlattenedMergedPages, pages =>
  memoize(id =>
    pages.find(page => page.id === id || page.ssConfigurationId === id)
  )
);

export const selectPageById = (state, id) => reselectPageById(state)(id);

const collectAncestors = (pageId, pages, ancestors = []) => {
  const page = find(pages, {id: pageId});
  return page
    ? [...collectAncestors(page.parent, pages, ancestors), page]
    : ancestors;
};

export const reselectGetAncestorsByPageId = createSelector(
  selectFlattenedMergedPages,
  pages => memoize(id => collectAncestors(id, pages))
);

export const getAncestorsByPageId = (state, id) =>
  reselectGetAncestorsByPageId(state)(id);

export const selectParentPageById = (state, id) => {
  const page = selectPageById(state, id);
  const pageId = (page && page.parent) || getPublicationGroupParentId(id);
  return selectPageById(state, pageId) || null;
};

const getMessageOrDefault = (formattedMessage, messages) =>
  messages[formattedMessage.props.id] || formattedMessage.props.defaultMessage;

export const selectStackTitleById = (state, id, messages) => {
  if (!id) return '';

  const page = selectPageById(state, id);

  return page
    ? isString(page.title)
      ? page.title
      : getMessageOrDefault(page.title, messages)
    : '';
};

export const selectIsStaticPage = (state, pageId) => pageId.startsWith('-');

export const selectIsRootPage = (state, pageId) =>
  Object.values(ROOT_PAGE_IDS).includes(pageId);

export const selectIsBibChild = (state, pageId) => {
  const page = selectPageById(state, pageId);
  return !!(page && page.parent === ROOT_PAGE_IDS.BIB);
};

export const selectIsNavigationLoaded = state =>
  state.navigation.pages.length > 0;
